import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { usePermissions } from "@/hooks/use-permissions";
import { PermissionLock, PermissionLockLoading } from "@/components/admin/PermissionLock";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, XCircle, ArrowLeft, Clock, Users, BookOpen } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/review")({
  component: ReviewQueuePage,
});

type Draft = {
  id: string;
  kind: string;
  status: string;
  tier: string;
  age_group: string;
  title_en: string;
  title_ar: string;
  description_en: string | null;
  description_ar: string | null;
  body_en: string | null;
  body_ar: string | null;
  value_tags: string[];
  tags: string[];
  reading_minutes: number | null;
  world_id: string | null;
  created_at: string;
};

type World = { id: string; code: string; name_en: string; name_ar: string };
type CharacterRow = { id: string; code: string; name_en: string; name_ar: string };

function ReviewQueuePage() {
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const perms = usePermissions();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [worlds, setWorlds] = useState<World[]>([]);
  const [chars, setChars] = useState<CharacterRow[]>([]);
  const [charsByContent, setCharsByContent] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: dRows }, { data: wRows }, { data: cRows }, { data: ccRows }] = await Promise.all([
      supabase
        .from("content_items")
        .select(
          "id,kind,status,tier,age_group,title_en,title_ar,description_en,description_ar,body_en,body_ar,value_tags,tags,reading_minutes,world_id,created_at",
        )
        .eq("status", "draft")
        .order("created_at", { ascending: false }),
      supabase.from("worlds").select("id,code,name_en,name_ar"),
      supabase.from("characters").select("id,code,name_en,name_ar"),
      supabase.from("content_characters").select("content_id,character_id"),
    ]);
    setDrafts((dRows ?? []) as Draft[]);
    setWorlds((wRows ?? []) as World[]);
    setChars((cRows ?? []) as CharacterRow[]);
    const map: Record<string, string[]> = {};
    for (const r of (ccRows ?? []) as { content_id: string; character_id: string }[]) {
      (map[r.content_id] ||= []).push(r.character_id);
    }
    setCharsByContent(map);
    setLoading(false);
  };

  useEffect(() => {
    if (!perms.loading && perms.canPublishContent) load();
  }, [perms.loading, perms.canPublishContent]);

  const selected = useMemo(() => drafts.find((d) => d.id === selectedId) ?? null, [drafts, selectedId]);

  if (perms.loading) return <PermissionLockLoading />;
  if (!perms.canPublishContent) return <PermissionLock />;

  if (selected) {
    return (
      <EditorPanel
        key={selected.id}
        draft={selected}
        worlds={worlds}
        chars={chars}
        characterIds={charsByContent[selected.id] ?? []}
        isAr={isAr}
        dir={dir}
        saving={saving}
        onBack={() => setSelectedId(null)}
        onSave={async (patch) => {
          setSaving(true);
          const { error } = await supabase
            .from("content_items")
            .update(patch as never)
            .eq("id", selected.id);
          setSaving(false);
          if (error) {
            toast.error(error.message);
            return;
          }
          toast.success(isAr ? "تم الحفظ" : "Saved");
          await load();
        }}
        onPublish={async () => {
          setSaving(true);
          const { error } = await supabase
            .from("content_items")
            .update({ status: "published", published_at: new Date().toISOString() })
            .eq("id", selected.id);
          setSaving(false);
          if (error) {
            toast.error(error.message);
            return;
          }
          toast.success(isAr ? "تم النشر" : "Published");
          setSelectedId(null);
          await load();
        }}
        onReject={async () => {
          setSaving(true);
          const { error } = await supabase
            .from("content_items")
            .update({ status: "archived" })
            .eq("id", selected.id);
          setSaving(false);
          if (error) {
            toast.error(error.message);
            return;
          }
          toast.success(isAr ? "تم الرفض" : "Rejected");
          setSelectedId(null);
          await load();
        }}

      />
    );
  }

  return (
    <div dir={dir} className="max-w-5xl mx-auto px-5 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{isAr ? "طابور المراجعة" : "Review queue"}</h1>
          <p className="text-sm text-foreground/60">
            {isAr
              ? "محتوى AI ينتظر مراجعة بشرية قبل النشر"
              : "AI-generated drafts awaiting human review before publish"}
          </p>
        </div>
        <Link to="/app/admin" className="text-sm underline">
          {isAr ? "لوحة الإدارة" : "Admin"}
        </Link>
      </div>

      {loading ? (
        <PermissionLockLoading />
      ) : drafts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-foreground/60">
            {isAr ? "لا توجد مسودات للمراجعة" : "No drafts to review"}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {drafts.map((d) => {
            const world = worlds.find((w) => w.id === d.world_id);
            const chIds = charsByContent[d.id] ?? [];
            const chNames = chIds
              .map((id) => chars.find((c) => c.id === id))
              .filter(Boolean)
              .map((c) => (isAr ? c!.name_ar : c!.name_en))
              .join(", ");
            return (
              <button
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                className="text-start"
              >
                <Card className="hover:bg-accent/30 transition-colors">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between gap-3">
                      <span>{isAr ? d.title_ar : d.title_en}</span>
                      <Badge variant="secondary">{d.kind}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-foreground/70 space-y-2">
                    <p className="line-clamp-2">{isAr ? d.description_ar : d.description_en}</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{d.age_group}</Badge>
                      <Badge variant="outline">{d.tier}</Badge>
                      {world && (
                        <Badge variant="outline">
                          🌍 {isAr ? world.name_ar : world.name_en}
                        </Badge>
                      )}
                      {chNames && (
                        <Badge variant="outline">
                          <Users className="size-3 me-1" />
                          {chNames}
                        </Badge>
                      )}
                      {d.reading_minutes && (
                        <Badge variant="outline">
                          <Clock className="size-3 me-1" />
                          {d.reading_minutes}m
                        </Badge>
                      )}
                      {(d.value_tags ?? []).map((t) => (
                        <Badge key={t} variant="outline">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditorPanel({
  draft,
  worlds,
  chars,
  characterIds,
  isAr,
  dir,
  saving,
  onBack,
  onSave,
  onPublish,
  onReject,
}: {
  draft: Draft;
  worlds: World[];
  chars: CharacterRow[];
  characterIds: string[];
  isAr: boolean;
  dir: "ltr" | "rtl";
  saving: boolean;
  onBack: () => void;
  onSave: (patch: Partial<Draft>) => Promise<void>;
  onPublish: () => Promise<void>;
  onReject: () => Promise<void>;
}) {
  const [titleEn, setTitleEn] = useState(draft.title_en);
  const [titleAr, setTitleAr] = useState(draft.title_ar);
  const [descEn, setDescEn] = useState(draft.description_en ?? "");
  const [descAr, setDescAr] = useState(draft.description_ar ?? "");
  const [bodyEn, setBodyEn] = useState(draft.body_en ?? "");
  const [bodyAr, setBodyAr] = useState(draft.body_ar ?? "");
  const world = worlds.find((w) => w.id === draft.world_id);
  const chNames = characterIds
    .map((id) => chars.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => (isAr ? c!.name_ar : c!.name_en))
    .join(", ");

  return (
    <div dir={dir} className="max-w-4xl mx-auto px-5 py-8 space-y-5">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="size-4 me-1" /> {isAr ? "رجوع" : "Back"}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={saving}
            onClick={() =>
              onSave({
                title_en: titleEn,
                title_ar: titleAr,
                description_en: descEn,
                description_ar: descAr,
                body_en: bodyEn,
                body_ar: bodyAr,
              })
            }
          >
            {isAr ? "حفظ المسودة" : "Save draft"}
          </Button>
          <Button variant="destructive" disabled={saving} onClick={onReject}>
            <XCircle className="size-4 me-1" /> {isAr ? "رفض" : "Reject"}
          </Button>
          <Button
            disabled={saving}
            onClick={async () => {
              await onSave({
                title_en: titleEn,
                title_ar: titleAr,
                description_en: descEn,
                description_ar: descAr,
                body_en: bodyEn,
                body_ar: bodyAr,
              });
              await onPublish();
            }}
          >
            <CheckCircle2 className="size-4 me-1" /> {isAr ? "اعتماد ونشر" : "Approve & publish"}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-4 flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">
            <BookOpen className="size-3 me-1" />
            {draft.kind}
          </Badge>
          <Badge variant="outline">{draft.age_group}</Badge>
          <Badge variant="outline">{draft.tier}</Badge>
          {world && (
            <Badge variant="outline">🌍 {isAr ? world.name_ar : world.name_en}</Badge>
          )}
          {chNames && (
            <Badge variant="outline">
              <Users className="size-3 me-1" /> {chNames}
            </Badge>
          )}
          {draft.reading_minutes && (
            <Badge variant="outline">
              <Clock className="size-3 me-1" />
              {draft.reading_minutes} min
            </Badge>
          )}
          {(draft.value_tags ?? []).map((t) => (
            <Badge key={t} variant="outline">
              {t}
            </Badge>
          ))}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <h3 className="font-semibold">English</h3>
          <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Title" />
          <Textarea
            value={descEn}
            onChange={(e) => setDescEn(e.target.value)}
            placeholder="Description"
            className="min-h-20"
          />
          <Textarea
            value={bodyEn}
            onChange={(e) => setBodyEn(e.target.value)}
            placeholder="Body"
            className="min-h-[400px] font-mono text-sm"
          />
        </div>
        <div className="space-y-3" dir="rtl">
          <h3 className="font-semibold">العربية</h3>
          <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} placeholder="العنوان" />
          <Textarea
            value={descAr}
            onChange={(e) => setDescAr(e.target.value)}
            placeholder="الوصف"
            className="min-h-20"
          />
          <Textarea
            value={bodyAr}
            onChange={(e) => setBodyAr(e.target.value)}
            placeholder="النص"
            className="min-h-[400px] font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );
}
